import AddCircleIcon from '@mui/icons-material/AddCircle';
import { Button, CircularProgress, Container, Pagination, Switch, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField } from '@mui/material';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { format } from 'date-fns';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import Swal from 'sweetalert2';
import { IPaginatedResponse } from '../../api/httpClient';
import { IGetMaterialPreviewResponse } from '../../api/material/iApiResponse';
import * as ScenarioHelper from '../../api/scenario/scenarioHelper';
import { EStatus } from '../../shared/enum';
import { IMaterial, IScenario } from '../../shared/iProject';
import { IGetVesselPreviewResponse } from '../../api/scenario/iApiResponse';
import { useNavigate } from 'react-router-dom';
import { route } from '../../route/Index';

interface IScenarioPanelProps {}

const ScenarioPanelContainer = styled(Container)`
    padding: 0;
    flex-grow: 1;
`;

const ScenarioPanel = (props: IScenarioPanelProps) => {
    let pageSize = 4;

    let timer = React.useRef<NodeJS.Timeout | null>(null);

    const navigate = useNavigate();

    const { t } = useTranslation();

    const [scenarioPreview, setScenarioPreview] = React.useState<IPaginatedResponse<IGetVesselPreviewResponse>>();
    const [isLoading, setIsLoading] = React.useState(false);
    const [pageNumber, setpageNumber] = React.useState(1);
    const [filterName, setFilterName] = React.useState<string | null>(null);

    const fetchScenarioPreview = async (page: number, filter?: string) => {
        setIsLoading(true);
        let res = await ScenarioHelper.getAllScenarioPreviewAsync(page, pageSize, filter);
        setScenarioPreview(res.data);
        setIsLoading(false);
    };

    React.useEffect(() => {
        fetchScenarioPreview(pageNumber);
    }, [pageNumber]);

    const handleGoToScenario = (vesselType: string) => navigate(route.backofficeScenario.replace(':param', vesselType));

    const handleChangeStatus = async (toTest: boolean, vesselType: string) => {
        let scenario = await ScenarioHelper.getScenarioByVesselTypeAsync(vesselType);
        let payload: IScenario = {
            ...scenario.data,
            status: toTest ? EStatus.Test : EStatus.Deleted,
        };
        let res = await ScenarioHelper.updateScenarioAsync(payload);
        if (res.status == 200) {
            fetchScenarioPreview(pageNumber);
            Swal.fire({
                title: t('updatedGeometry'),
                icon: 'success',
                toast: true,
                position: 'top',
                showConfirmButton: false,
                timer: 2000,
            });
        }
    };

    React.useEffect(() => {
        if (filterName !== null) {
            if (timer.current) {
                clearTimeout(timer.current);
            }
            timer.current = setTimeout(() => {
                fetchScenarioPreview(1, filterName);
            }, 1000);
        }
    }, [filterName]);

    return (
        <ScenarioPanelContainer maxWidth='md'>
            <Stack height='100%' direction='column' spacing={4}>
                <Stack direction='row' justifyContent='space-between'>
                    <Typography variant='h1' gutterBottom>
                        {t('scenario')}
                    </Typography>
                    <Button variant='orange' endIcon={<AddCircleIcon />} onClick={() => navigate(route.backofficeScenario.replace(':param', 'newScenario'))}>
                        {t('addNewScenario')}
                    </Button>
                </Stack>
                <TextField
                    id='input-search'
                    placeholder={t('searchName')}
                    InputProps={{
                        type: 'search',
                    }}
                    onChange={e => setFilterName(e.target.value)}
                />
                {isLoading && <CircularProgress />}
                {!isLoading && scenarioPreview?.value?.length! > 0 && (
                    <>
                        <Stack flexGrow={1}>
                            <TableContainer>
                                <Table sx={{ minWidth: 650 }} size='small' aria-label='simple table'>
                                    <TableHead variant='greenLightWithoutBorder'>
                                        <TableRow>
                                            <TableCell>{t('name')}</TableCell>
                                            <TableCell align='center'>{t('lastUpdate')}</TableCell>
                                            <TableCell align='right'>{t('enabled')}</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {scenarioPreview!.value.map(x => (
                                            <TableRow key={x.id} hover sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                                                <TableCell onClick={() => handleGoToScenario(x.vesselType)} component='th' scope='row'>
                                                    {x.vesselType}
                                                </TableCell>
                                                <TableCell onClick={() => handleGoToScenario(x.vesselType)} align='center'>
                                                    {format(x.lastUpdateDate, 'dd MMMM yyyy')}
                                                </TableCell>
                                                <TableCell align='right'>
                                                    <Switch
                                                        onChange={e => handleChangeStatus(e.target.checked, x.vesselType)}
                                                        aria-label={`switch ${x.vesselType}`}
                                                        checked={x.status == EStatus.Public || x.status == EStatus.Test}
                                                    />
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Stack>
                        {scenarioPreview!.pageCount > 1 && (
                            <Stack alignItems='end'>
                                <Pagination
                                    showFirstButton
                                    showLastButton
                                    variant='outlined'
                                    shape='rounded'
                                    page={scenarioPreview?.pageNumber}
                                    count={scenarioPreview?.pageCount}
                                    onChange={(e, value) => setpageNumber(value)}
                                />
                            </Stack>
                        )}
                    </>
                )}
                {!isLoading && scenarioPreview?.value?.length! == 0 && <div>Empty</div>}
            </Stack>
        </ScenarioPanelContainer>
    );
};

export default ScenarioPanel;
